/**
 * Batch Audit Trail Tab Component
 * Displays audit logs for batch operations
 *
 * @author SAUBHAGYA Development Team
 * @since US-BATCH-002
 */

import { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';
import {
  AlertCircle,
  CheckCircle,
  Edit,
  Eye,
  Loader2,
} from 'lucide-react';

interface BatchAuditTrailTabProps {
  batchId: number;
}

// Mock audit log entry - should match backend response
interface AuditLogEntry {
  id: string;
  timestamp: string;
  action: string;
  performedBy: string;
  details: string;
  status: 'success' | 'warning' | 'error' | 'info';
}

// Action type configurations
const ActionConfig: Record<string, { icon: React.ReactNode; color: string; bgColor: string }> = {
  CREATED: {
    icon: <CheckCircle className="w-4 h-4" />,
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
  UPDATED: {
    icon: <Edit className="w-4 h-4" />,
    color: 'text-blue-700',
    bgColor: 'bg-blue-100',
  },
  ALLOCATED: {
    icon: <CheckCircle className="w-4 h-4" />,
    color: 'text-purple-700',
    bgColor: 'bg-purple-100',
  },
  QUALITY_UPDATED: {
    icon: <Edit className="w-4 h-4" />,
    color: 'text-orange-700',
    bgColor: 'bg-orange-100',
  },
  PESO_COMPLIANCE_ADDED: {
    icon: <CheckCircle className="w-4 h-4" />,
    color: 'text-green-700',
    bgColor: 'bg-green-100',
  },
  VIEWED: {
    icon: <Eye className="w-4 h-4" />,
    color: 'text-gray-700',
    bgColor: 'bg-gray-100',
  },
};

export function BatchAuditTrailTab({ batchId }: BatchAuditTrailTabProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>([]);
  const [expandedLogId, setExpandedLogId] = useState<string | null>(null);

  // Mock data - in real implementation, fetch from API
  const mockAuditLogs: AuditLogEntry[] = [
    {
      id: '1',
      timestamp: new Date().toISOString(),
      action: 'CREATED',
      performedBy: 'system',
      details: 'Batch created with initial quantity of 5000 KG',
      status: 'success',
    },
    {
      id: '2',
      timestamp: new Date(Date.now() - 1000 * 60 * 60).toISOString(),
      action: 'PESO_COMPLIANCE_ADDED',
      performedBy: 'admin@saubhagya.com',
      details: 'PESO certificate added - Certificate Number: PESO-2025-XYZ123',
      status: 'success',
    },
    {
      id: '3',
      timestamp: new Date(Date.now() - 1000 * 60 * 60 * 24).toISOString(),
      action: 'QUALITY_UPDATED',
      performedBy: 'quality_inspector@saubhagya.com',
      details: 'Quality grade updated to A with purity 98.5%',
      status: 'success',
    },
  ];

  // Simulate loading audit logs
  const loadAuditLogs = async () => {
    setIsLoading(true);
    // Simulate API call delay
    await new Promise((resolve) => setTimeout(resolve, 1000));
    setAuditLogs(mockAuditLogs);
    setIsLoading(false);
  };

  // Load logs on component mount
  const hasLoaded = auditLogs.length > 0 || isLoading;
  if (!hasLoaded && !isLoading) {
    loadAuditLogs();
  }

  return (
    <div className="space-y-6">
      {/* Audit Information */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Audit Trail Information</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-600 mb-4">
            This audit trail shows all actions performed on this batch, including creation,
            updates, quality changes, and compliance certifications.
          </p>
          <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
            <p className="text-sm text-blue-800">
              <span className="font-semibold">Batch ID:</span> {batchId}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Audit Logs Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-lg">Activity Logs</CardTitle>
            {isLoading && <Loader2 className="w-5 h-5 animate-spin text-blue-600" />}
          </div>
        </CardHeader>
        <CardContent>
          {auditLogs.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Timestamp</TableHead>
                    <TableHead>Action</TableHead>
                    <TableHead>Performed By</TableHead>
                    <TableHead>Details</TableHead>
                    <TableHead className="text-center w-12">Expand</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {auditLogs.map((log) => {
                    const config = ActionConfig[log.action] || {
                      icon: <AlertCircle className="w-4 h-4" />,
                      color: 'text-gray-700',
                      bgColor: 'bg-gray-100',
                    };
                    const isExpanded = expandedLogId === log.id;

                    return (
                      <TableRow key={log.id} className="hover:bg-gray-50">
                        <TableCell className="font-medium">
                          {format(new Date(log.timestamp), 'MMM dd, yyyy HH:mm:ss')}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className={cn(config.color)}>{config.icon}</span>
                            <Badge className={cn(config.bgColor, config.color, 'border-none')}>
                              {log.action.replace(/_/g, ' ')}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm font-medium">{log.performedBy}</span>
                        </TableCell>
                        <TableCell>
                          <span className="text-sm text-gray-600 truncate block max-w-xs">
                            {log.details}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() =>
                              setExpandedLogId(isExpanded ? null : log.id)
                            }
                            className="text-xs"
                          >
                            {isExpanded ? '−' : '+'}
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>

              {/* Expanded Details */}
              {expandedLogId && (
                <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
                  {auditLogs
                    .filter((log) => log.id === expandedLogId)
                    .map((log) => (
                      <div key={log.id} className="space-y-3">
                        <div>
                          <h4 className="font-semibold text-gray-900 mb-2">
                            Full Details
                          </h4>
                          <p className="text-sm text-gray-700 whitespace-pre-wrap">
                            {log.details}
                          </p>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600 font-medium">Timestamp:</span>
                            <p className="text-gray-900">
                              {format(new Date(log.timestamp), 'PPpp')}
                            </p>
                          </div>
                          <div>
                            <span className="text-gray-600 font-medium">User:</span>
                            <p className="text-gray-900">{log.performedBy}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-600">No audit logs available</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Timeline View */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Timeline View</CardTitle>
        </CardHeader>
        <CardContent>
          {auditLogs.length > 0 ? (
            <div className="space-y-4">
              {auditLogs.map((log, index) => {
                const config = ActionConfig[log.action] || {
                  icon: <AlertCircle className="w-4 h-4" />,
                  color: 'text-gray-700',
                  bgColor: 'bg-gray-100',
                };

                return (
                  <div key={log.id} className="flex gap-4">
                    {/* Timeline dot and line */}
                    <div className="flex flex-col items-center">
                      <div
                        className={cn(
                          'w-10 h-10 rounded-full flex items-center justify-center',
                          config.bgColor,
                          config.color,
                          'border-2 border-white shadow-md'
                        )}
                      >
                        {config.icon}
                      </div>
                      {index < auditLogs.length - 1 && (
                        <div className="w-1 h-8 bg-gray-300 my-2" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 py-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-semibold text-gray-900">
                            {log.action.replace(/_/g, ' ')}
                          </h4>
                          <p className="text-sm text-gray-600 mt-1">
                            {log.details}
                          </p>
                        </div>
                        <span className="text-xs text-gray-500 whitespace-nowrap ml-4">
                          {format(new Date(log.timestamp), 'MMM dd HH:mm')}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        By {log.performedBy}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-600">
              No activity to display
            </div>
          )}
        </CardContent>
      </Card>

      {/* Export Audit Trail */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Export Options</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Button variant="outline" size="sm">
              Export as CSV
            </Button>
            <Button variant="outline" size="sm">
              Export as PDF
            </Button>
            <Button variant="outline" size="sm">
              Export as JSON
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
